/** The entity fields the layout needs; satisfied by the SDK's catalog entity type. */
export interface LayoutEntity {
	ref: string;
	name: string;
	projectRepositoryId?: string | null;
	repositoryFullName?: string | null;
	isExternal: boolean;
}

/** The relation fields the layout needs; satisfied by the SDK's catalog relation type. */
export interface LayoutRelation {
	sourceRef: string;
	targetRef: string;
	type: string;
}

/** Repository-view node id for entities no linked repository defines. */
export const EXTERNAL_REPOSITORY_ID = "external";

/**
 * Left-to-right layered positions: a relation's target is drawn right of its source, so dependents
 * sit on the left and what they depend on (APIs, resources, the system they belong to) on the right.
 * Each node goes one column past its furthest-left source (longest path); a cycle is cut where it is
 * first revisited. Within a column, nodes are grouped by `groupOf` and then sorted by label, so a
 * repository's entities stay together.
 */
export function layeredLayout(
	ids: string[],
	edges: { source: string; target: string }[],
	labelOf: (id: string) => string,
	groupOf: (id: string) => string = () => "",
	spacing = { x: 320, y: 120 },
): Record<string, { x: number; y: number }> {
	const sources = new Map<string, string[]>(ids.map((id) => [id, []]));
	for (const e of edges) sources.get(e.target)?.push(e.source);

	const columns = new Map<string, number>();
	const column = (id: string, visiting: Set<string>): number => {
		const known = columns.get(id);
		if (known !== undefined) return known;
		if (visiting.has(id)) return 0;
		visiting.add(id);
		const incoming = sources.get(id) ?? [];
		const value = incoming.length
			? Math.max(...incoming.map((s) => column(s, visiting))) + 1
			: 0;
		visiting.delete(id);
		columns.set(id, value);
		return value;
	};
	for (const id of ids) column(id, new Set());

	const byColumn = new Map<number, string[]>();
	for (const id of ids) {
		const c = columns.get(id) ?? 0;
		byColumn.set(c, [...(byColumn.get(c) ?? []), id]);
	}

	const positions: Record<string, { x: number; y: number }> = {};
	for (const [c, members] of byColumn) {
		members.sort(
			(a, b) =>
				groupOf(a).localeCompare(groupOf(b)) ||
				labelOf(a).localeCompare(labelOf(b)),
		);
		const top = (-(members.length - 1) * spacing.y) / 2;
		members.forEach((id, i) => {
			positions[id] = { x: c * spacing.x, y: top + i * spacing.y };
		});
	}
	return positions;
}

/** One repository in the repository view. */
export interface RepositoryNode {
	id: string;
	label: string;
	entityCount: number;
}

/** A repository-to-repository link: every entity relation that crosses between the two. */
export interface RepositoryEdge {
	source: string;
	target: string;
	/** Relation types crossing this way, de-duplicated and sorted. */
	types: string[];
	count: number;
}

/**
 * Rolls the entity graph up to repositories: one node per defining repository (plus one for
 * external entities, if any), one edge per ordered repository pair with at least one relation
 * crossing between them. Relations inside a single repository are dropped.
 */
export function rollUpToRepositories(
	entities: LayoutEntity[],
	relations: LayoutRelation[],
): { nodes: RepositoryNode[]; edges: RepositoryEdge[] } {
	const repositoryOf = new Map<string, string>();
	const nodes = new Map<string, RepositoryNode>();
	for (const e of entities) {
		const id = e.isExternal
			? EXTERNAL_REPOSITORY_ID
			: (e.projectRepositoryId ?? EXTERNAL_REPOSITORY_ID);
		repositoryOf.set(e.ref, id);
		const node = nodes.get(id) ?? {
			id,
			label:
				id === EXTERNAL_REPOSITORY_ID
					? "Not in this project"
					: (e.repositoryFullName ?? id),
			entityCount: 0,
		};
		node.entityCount++;
		nodes.set(id, node);
	}

	const edges = new Map<string, RepositoryEdge>();
	for (const r of relations) {
		const source = repositoryOf.get(r.sourceRef);
		const target = repositoryOf.get(r.targetRef);
		if (!source || !target || source === target) continue;
		const key = `${source}\u0000${target}`;
		const edge = edges.get(key) ?? { source, target, types: [], count: 0 };
		if (!edge.types.includes(r.type))
			edge.types = [...edge.types, r.type].sort();
		edge.count++;
		edges.set(key, edge);
	}

	return { nodes: [...nodes.values()], edges: [...edges.values()] };
}

/** True when the relation joins entities defined in two different linked repositories. */
export function crossesRepositories(
	relation: LayoutRelation,
	repositoryOf: Map<string, string | null | undefined>,
): boolean {
	const source = repositoryOf.get(relation.sourceRef);
	const target = repositoryOf.get(relation.targetRef);
	return !!source && !!target && source !== target;
}
